import { useRef, useState, useCallback, useEffect } from 'react';
import { reportCrash } from '../../utils/crash-reporter';

/**
 * One entry per chunk. `url` is an absolute URL to a standalone MP3 in S3
 * (or null if the chunk has no audio — e.g. the same-language short-circuit
 * in the backend enrichment pipeline). The hook feeds the URL directly into
 * a native <audio> element, which handles streaming, range requests, and
 * HTTP caching without the hook touching any bytes.
 */
interface ChunkAudio {
  url: string;
}

interface UseAudioPlayerOptions {
  chunkAudios: (ChunkAudio | null)[];
  speed: number;
  onChunkComplete?: (chunkIndex: number) => void;
  onLookahead?: (fromChunkIndex: number) => void;
  onError?: (message: string) => void;
  onActiveChunkChange?: (chunkIndex: number | null) => void;
  scrollToChunk?: (chunkIndex: number) => void;
  /**
   * Returns the chunk the user is currently focused on (by the unified
   * focus-offset function — see useVisibleChunk). When a chunk ends, if
   * the user has scrolled away from the playing chunk, the focused chunk
   * becomes the next chunk to play (rather than `chunkIndex + 1`);
   * `scrollToChunk` no-ops on an already-focused chunk, so the viewport
   * stays put. The focus model has no dead zone, so every chunk —
   * including the last few anchored by the page bottom — has a unique
   * scroll position that puts it in focus; backward focus-follow can be
   * trusted everywhere.
   */
  getFocusedChunkIndex?: () => number;
}

const PRE_SCROLL_DELAY_MS = 500;
const POST_SCROLL_DELAY_MS = 500;
const POST_MUTE_DELAY_MS = 500;

export function useAudioPlayer({
  chunkAudios,
  speed,
  onChunkComplete,
  onLookahead,
  onError,
  onActiveChunkChange,
  scrollToChunk,
  getFocusedChunkIndex,
}: UseAudioPlayerOptions) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const endedListenerRef = useRef<(() => void) | null>(null);
  const [activeChunkIndex, setActiveChunkIndex] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const isTransitioningRef = useRef(false);
  const activeChunkRef = useRef<number | null>(null);
  const transitionTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const cancelledRef = useRef(false);

  const cancelTransitionTimers = useCallback(() => {
    for (const id of transitionTimersRef.current) {
      clearTimeout(id);
    }
    transitionTimersRef.current = [];
    cancelledRef.current = true;
  }, []);

  // Create audio element once
  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;

    const onAudioError = () => {
      const e = audio.error;
      reportCrash({
        feature: 'audio-element',
        errorMessage: e ? `${e.code}: ${e.message}` : 'unknown audio error',
        context: {
          chunkIndex: activeChunkRef.current,
          audioTime: audio.currentTime,
          src: audio.src.substring(audio.src.lastIndexOf('/') + 1),
        },
      });
    };
    audio.addEventListener('error', onAudioError);

    return () => {
      audio.removeEventListener('error', onAudioError);
      audio.pause();
      audio.src = '';
      audioRef.current = null;
      cancelTransitionTimers();
    };
  }, [cancelTransitionTimers]);

  // Update speed reactively
  const safeSpeed = Number.isFinite(speed) && speed > 0 ? speed : 1.0;
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = safeSpeed;
    }
  }, [safeSpeed]);

  // Keep ref in sync with state
  useEffect(() => {
    activeChunkRef.current = activeChunkIndex;
    onActiveChunkChange?.(activeChunkIndex);
  }, [activeChunkIndex, onActiveChunkChange]);

  // Trigger lookahead from a given chunk
  const triggerLookahead = useCallback(
    (fromIndex: number) => {
      onLookahead?.(fromIndex);
    },
    [onLookahead]
  );

  /** Helper: schedule a timeout and track it for cancellation. */
  const scheduleTimer = useCallback((fn: () => void, ms: number): ReturnType<typeof setTimeout> => {
    const id = setTimeout(fn, ms);
    transitionTimersRef.current.push(id);
    return id;
  }, []);

  /** Helper: cancellable delay that rejects if timers are cancelled. */
  const cancellableDelay = useCallback((ms: number): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      cancelledRef.current = false;
      const id = setTimeout(() => {
        if (cancelledRef.current) {
          reject(new Error('cancelled'));
        } else {
          resolve();
        }
      }, ms);
      transitionTimersRef.current.push(id);
    });
  }, []);

  const playChunk = useCallback(
    async (chunkIndex: number) => {
      const audio = audioRef.current;
      if (!audio) return;

      const chunkAudio = chunkAudios[chunkIndex];

      if (!chunkAudio || !chunkAudio.url) {
        // Silently skip chunks with missing audio (e.g. same-language articles
        // where the backend short-circuited TTS, or a chunk whose MP3 upload
        // failed during enrichment and was left without an audioUrl).
        const nextIndex = chunkIndex + 1;
        if (nextIndex < chunkAudios.length) {
          playChunk(nextIndex);
        } else {
          setIsPlaying(false);
          setActiveChunkIndex(null);
        }
        return;
      }

      scrollToChunk?.(chunkIndex);

      // Stop current and remove old ended listener
      audio.pause();
      if (endedListenerRef.current) {
        audio.removeEventListener('ended', endedListenerRef.current);
        endedListenerRef.current = null;
      }

      // Point the <audio> element at the chunk's MP3 URL. The browser
      // handles fetching, streaming, range requests, and HTTP caching —
      // we don't decode or buffer anything in JS.
      audio.src = chunkAudio.url;
      audio.playbackRate = safeSpeed;

      // Trigger lookahead
      triggerLookahead(chunkIndex);

      // Set up ended listener
      const endedListener = () => {
        try {
        endedListenerRef.current = null;
        onChunkComplete?.(chunkIndex);

        const focused = getFocusedChunkIndex?.();
        const followFocus =
          focused !== undefined &&
          focused !== chunkIndex &&
          focused < chunkAudios.length;
        const nextIndex = followFocus ? focused : chunkIndex + 1;
        if (nextIndex < chunkAudios.length) {
          if (isTransitioningRef.current) return;
          isTransitioningRef.current = true;

          scheduleTimer(() => {
            scrollToChunk?.(nextIndex);
            scheduleTimer(() => {
              isTransitioningRef.current = false;
              playChunk(nextIndex);
            }, POST_SCROLL_DELAY_MS);
          }, PRE_SCROLL_DELAY_MS);
        } else {
          setIsPlaying(false);
          setActiveChunkIndex(null);
        }
        } catch (err) {
          reportCrash({
            feature: 'audio-ended',
            errorMessage: err instanceof Error ? err.message : String(err),
            errorStack: err instanceof Error ? err.stack : undefined,
            context: { chunkIndex, audioTime: audio.currentTime },
          });
          setIsPlaying(false);
          setActiveChunkIndex(null);
        }
      };
      audio.addEventListener('ended', endedListener, { once: true });
      endedListenerRef.current = endedListener;

      setActiveChunkIndex(chunkIndex);
      setIsPlaying(true);

      // Brief pause after muting so the visual transition settles before audio begins
      try {
        await cancellableDelay(POST_MUTE_DELAY_MS);
      } catch {
        return; // Timers cancelled — exit cleanly
      }

      try {
        await audio.play();
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // Retry once
        try {
          audio.currentTime = 0;
          await audio.play();
        } catch (retryErr) {
          if (retryErr instanceof DOMException && retryErr.name === 'AbortError') return;
          reportCrash({
            feature: 'audio-play',
            errorMessage: retryErr instanceof Error ? retryErr.message : String(retryErr),
            errorStack: retryErr instanceof Error ? retryErr.stack : undefined,
            context: { chunkIndex, audioTime: audio.currentTime },
          });
          onError?.('Audio playback failed');
          setIsPlaying(false);
        }
      }
    },
    [chunkAudios, speed, onChunkComplete, onError, triggerLookahead, scrollToChunk, getFocusedChunkIndex, scheduleTimer, cancellableDelay]
  );

  const pause = useCallback(() => {
    audioRef.current?.pause();
    // Cancel any pending chunk-transition timers. Without this, a pause
    // pressed during the inter-chunk gap (PRE/POST scroll delay or
    // POST_MUTE_DELAY) would be undone by the timer firing and auto-starting
    // the next chunk.
    cancelTransitionTimers();
    isTransitioningRef.current = false;
    setIsPlaying(false);
  }, [cancelTransitionTimers]);

  const resume = useCallback(async () => {
    const audio = audioRef.current;
    const activeIndex = activeChunkRef.current;
    if (!audio || activeIndex === null) return;

    // If the user paused during the inter-chunk gap, the current chunk's
    // audio has already finished. Replaying it would restart the same chunk;
    // advance to the next one instead so resume picks up where the automatic
    // transition would have.
    if (audio.ended || !audio.src) {
      const nextIndex = activeIndex + 1;
      if (nextIndex < chunkAudios.length) {
        playChunk(nextIndex);
      }
      return;
    }

    setIsPlaying(true);
    try {
      await audio.play();
    } catch {
      setIsPlaying(false);
    }
  }, [chunkAudios, playChunk]);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = '';
    }
    if (endedListenerRef.current) {
      audioRef.current?.removeEventListener('ended', endedListenerRef.current);
      endedListenerRef.current = null;
    }
    cancelTransitionTimers();
    isTransitioningRef.current = false;
    setIsPlaying(false);
    setActiveChunkIndex(null);
  }, [cancelTransitionTimers]);

  const playFromHere = useCallback(
    (visibleChunkIndex: number) => {
      cancelTransitionTimers();
      isTransitioningRef.current = false;
      playChunk(visibleChunkIndex);
    },
    [cancelTransitionTimers, playChunk]
  );

  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      pause();
    } else if (activeChunkRef.current !== null) {
      resume();
    } else {
      playChunk(0);
    }
  }, [isPlaying, pause, resume, playChunk]);

  const getCurrentTime = useCallback((): number => {
    return (audioRef.current?.currentTime ?? 0) * 1000;
  }, []);

  return {
    isPlaying,
    activeChunkIndex,
    playChunk,
    pause,
    resume,
    stop,
    playFromHere,
    togglePlayPause,
    getCurrentTime,
    audioRef,
  };
}
